import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import * as React from 'react';
import classNames from 'classnames';
import styles from './Button.module.scss.js';
import { applyCommonProps } from '../utility/commonProps.js';
import { getTarget } from '../utility/getTarget.js';
import { onAnchorKeyDown } from '../utility/onAnchorKeyDown.js';

var _excluded = ["className", "children", "isFullWidth", "leading", "size", "trailing", "variant"],
  _excluded2 = ["href", "onClick", "onKeyDown", "target"],
  _excluded3 = ["type"];
var isAnchor = function isAnchor(props) {
  return 'href' in props;
};
var _Button = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _applyCommonProps = applyCommonProps(props, Button),
    className = _applyCommonProps.className,
    children = _applyCommonProps.children,
    _applyCommonProps$isF = _applyCommonProps.isFullWidth,
    isFullWidth = _applyCommonProps$isF === void 0 ? false : _applyCommonProps$isF,
    leading = _applyCommonProps.leading,
    _applyCommonProps$siz = _applyCommonProps.size,
    size = _applyCommonProps$siz === void 0 ? 'small' : _applyCommonProps$siz,
    trailing = _applyCommonProps.trailing,
    _applyCommonProps$var = _applyCommonProps.variant,
    variant = _applyCommonProps$var === void 0 ? 'secondary' : _applyCommonProps$var,
    rest = _objectWithoutProperties(_applyCommonProps, _excluded);
  var classes = classNames(styles.button, variant === 'destructive' && styles.destructive, variant === 'primary' && styles.primary, variant === 'secondary' && styles.secondary, variant === 'tertiary' && styles.tertiary, isFullWidth && styles.isFullWidth, size === 'medium' && styles.medium, size === 'small' && styles.small, className);
  var content = /*#__PURE__*/React.createElement(React.Fragment, null, leading && /*#__PURE__*/React.createElement("span", {
    className: styles.leading
  }, leading), children, trailing && /*#__PURE__*/React.createElement("span", {
    className: styles.trailing
  }, trailing));
  if (isAnchor(props)) {
    var _ref = rest,
      href = _ref.href,
      onClick = _ref.onClick,
      anchorOnKeyDown = _ref.onKeyDown,
      target = _ref.target,
      anchorProps = _objectWithoutProperties(_ref, _excluded2);

    // Looks like a button should act like a button.
    //
    // @see {@link https://jira.walmart.com/browse/LD-374?focusedCommentId=12802856&page=com.atlassian.jira.plugin.system.issuetabpanels%3Acomment-tabpanel#comment-12802856}
    var onKeyDown = onAnchorKeyDown({
      href: href,
      onClick: onClick,
      onKeyDown: anchorOnKeyDown
    });
    return /*#__PURE__*/React.createElement("a", _extends({
      className: classes,
      href: href,
      onClick: onClick,
      onKeyDown: onKeyDown,
      ref: ref
    }, getTarget(target), anchorProps), content);
  }
  var _ref2 = rest,
    _ref2$type = _ref2.type,
    type = _ref2$type === void 0 ? 'button' : _ref2$type,
    buttonProps = _objectWithoutProperties(_ref2, _excluded3);
  return /*#__PURE__*/React.createElement("button", _extends({
    className: classes,
    ref: ref,
    type: type
  }, buttonProps), content);
});
_Button.displayName = 'Button';

/**
 * Buttons allow users to take actions and make choices, with a single press or a tap. Buttons are used as calls-to-action (CTA) across the experience. They are organized in the order or their importance: primary, secondary, and tertiary.
 *
 * - {@link https://livingdesign.walmart.com/components/button/ Guidelines}
 * - {@link https://livingdesign.walmart.com/components/button/react/ React documentation}
 *
 * ### Content
 *
 * #### Leading and trailing
 *
 * The Button accepts optional leading and trailing content through the `leading` and `trailing' props:
 *
 * ```tsx
 * import * as React from "react";
 * import { Button } from "@walmart-web/livingdesign-components";
 * import * as Icon from "@livingdesign/icons";
 *
 * export default () => {
 *   return (
 *     <Button
 *       leading={<Icon.Cart />}
 *     >
 *       Add to cart
 *     </Button>
 *   );
 * }
 *```
 *
 * ### Features
 *
 * #### Size
 *
 * The Button comes with three sizes:
 *
 * - `small` (default)
 * - `medium`
 *
 * Set the Button's size with the `size` prop:
 *
 * ```tsx
 * <Button size="small">Home</Button>
 * <Button size="medium">Home</Button>
 *```
 * #### Variant
 *
 * The Button comes with four variants:
 *
 * - `primary`: Primary buttons call out the primary action on screen. There should be only a single primary button on any given screen.
 * - `secondary` (default): Secondary buttons are used for other, non-primary actions on a given screen. They are in the middle with regard to “loudness”, sitting between primary and tertiary.
 * - `tertiary`: Tertiary buttons are used for actions requiring less “loudness”. Tertiary buttons have built in padding that is invisible but needs to be respected when laying out designs.
 * - `destructive`: Destructive is for a button that has dangerous or negative connotation.
 *
 * ```tsx
 * <Button variant="primary">Submit</Button>
 * <Button variant="secondary">Submit</Button>
 * <Button variant="tertiary">Submit</Button>
 * <Button variant="destructive">Submit</Button>
 * ```
 *
 * ### Interaction
 *
 * #### Linking
 *
 * The Button can be configured to be an `HTMLAnchorElement` or an `HTMLButtonElement` depending on the props passed to it.
 *
 * Provide an `href` prop to make the Button an `HTMLAnchorElement`:
 *
 * ```tsx
 * <Button href="https://www.walmart.com/">
 *   Home
 * </Button>
 * ```
 *
 * Otherwise, the Button will be rendered as an `HTMLButtonElement` and accept the `type` and `disabled` attributes as props:
 *
 * ```tsx
 * <Button type="submit" disabled>Submit</Button>
 * ```
 *
 * ### Layout
 *
 * #### Width
 *
 * The Button can be configured to fill the width of its parent using the `isFullWidth` prop:
 *
 * ```tsx
 * <Button isFullWidth>Content</Button>
 * ```
 */
var Button = _Button;

export { Button };
